<template>
  <div>
    <CRow>
      <CCol sm="12" md="10">
        <CCard>
          <CCardHeader>
            Apps Settings
          </CCardHeader>
          <CCardBody>
            <div class="systemsettings-tabs-wrapper">
              <CTabs variant="pills" class="systemsettings-tabs">
                <CTab title="FAQ (Apps)">
                <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
                  <div class="d-flex align-items-center mb-2">
                    <span class="font-weight-bold mr-3">Locale</span>
                    <CButtonGroup size="sm">
                      <CButton
                        v-for="locale in faqLocales"
                        :key="locale"
                        class="text-uppercase"
                        :color="locale === activeFaqLocale ? 'info' : 'secondary'"
                        :pressed="locale === activeFaqLocale"
                        @click="setFaqLocale(locale)"
                      >
                        {{ locale }}
                      </CButton>
                    </CButtonGroup>
                    <CBadge v-if="faqDirty[activeFaqLocale]" color="warning" class="ml-3">Unsaved changes</CBadge>
                  </div>
                  <div class="mb-2 text-right">
                    <CButton color="info" variant="outline" size="sm" class="mr-2" @click="openFaqModal('add')">
                      Add FAQ
                    </CButton>
                    <CButton
                      color="success"
                      size="sm"
                      :disabled="!faqDirty[activeFaqLocale] || faqSavingLocale === activeFaqLocale"
                      @click="saveFaqEntries"
                    >
                      {{ faqSavingLocale === activeFaqLocale ? 'Saving...' : 'Save All' }}
                    </CButton>
                  </div>
                </div>
                <div v-if="faqLoadingLocales[activeFaqLocale]" class="text-center text-muted py-4">
                  Loading FAQ data...
                </div>
                <template v-else>
                  <div class="table-responsive faq-table">
                    <table class="table table-hover table-outline">
                      <thead>
                        <tr>
                          <th style="width: 110px;">Order</th>
                          <th>Question</th>
                          <th>Answer Preview</th>
                          <th style="width: 170px;" class="text-right">Actions</th>
                        </tr>
                      </thead>
                      <draggable
                        tag="tbody"
                        :list="faqData[activeFaqLocale]"
                        handle=".faq-row-handle"
                        @end="onFaqReorder"
                      >
                        <tr v-for="(entry, index) in currentFaqEntries" :key="`faq-${activeFaqLocale}-${index}`">
                          <td>
                            <span class="faq-row-handle mr-2" title="Drag to reorder">
                              <CIcon name="cil-menu"/>
                            </span>
                            {{ index + 1 }}
                          </td>
                          <td>
                            <div class="font-weight-bold">{{ entry.question }}</div>
                            <small class="text-muted">{{ (entry.question || '').length }} / 120</small>
                          </td>
                          <td>
                            <div class="faq-answer-preview">{{ previewAnswer(entry.answer) }}</div>
                          </td>
                          <td class="text-right">
                            <CButton size="sm" color="info" variant="outline" class="mr-2" @click="openFaqModal('edit', index)">
                              Edit
                            </CButton>
                            <CButton size="sm" color="danger" variant="outline" @click="deleteFaq(index)">
                              Delete
                            </CButton>
                          </td>
                        </tr>
                      </draggable>
                      <tbody v-if="!currentFaqEntries.length">
                        <tr>
                          <td colspan="4" class="text-center text-muted py-4">
                            No FAQ entries for this locale yet.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p class="text-muted small mb-0">
                    Drag rows with the handle to change ordering. Changes are saved separately for each locale.
                  </p>
                </template>
              </CTab>
              <CTab title="Giving Information (Apps)">
                <CRow>
                  <CCol md="6">
                    <CInput
                      label="Bank Name"
                      placeholder="e.g. BCA"
                      v-model="givingForm.bank_name"
                      @input="markGivingDirty()"
                    />
                    <small v-if="givingErrors.bank_name" class="text-danger d-block mb-2">{{ givingErrors.bank_name }}</small>
                    <CInput
                      label="Account Number"
                      placeholder="Bank account number"
                      v-model="givingForm.account_number"
                      @input="markGivingDirty()"
                    />
                    <small v-if="givingErrors.account_number" class="text-danger d-block mb-2">{{ givingErrors.account_number }}</small>
                    <CInput
                      label="Account Holder"
                      placeholder="Name shown on the account"
                      v-model="givingForm.account_holder"
                      @input="markGivingDirty()"
                    />
                    <small v-if="givingErrors.account_holder" class="text-danger d-block mb-2">{{ givingErrors.account_holder }}</small>
                    <div class="form-group">
                      <label class="font-weight-bold">Currency</label>
                      <select class="form-control" v-model="givingForm.currency" @change="markGivingDirty()">
                        <option
                          v-for="option in currencyOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.text }}
                        </option>
                      </select>
                      <small v-if="givingErrors.currency" class="text-danger d-block">{{ givingErrors.currency }}</small>
                    </div>
                    <CInput
                      label="QR Image URL"
                      placeholder="https://..."
                      v-model="givingForm.qr_image_url"
                      @input="markGivingDirty()"
                    />
                    <small v-if="givingErrors.qr_image_url" class="text-danger d-block mb-2">{{ givingErrors.qr_image_url }}</small>
                    <CTextarea
                      label="Notes (optional)"
                      rows="4"
                      placeholder="Additional notes shown in the app"
                      v-model="givingForm.notes"
                      @input="markGivingDirty()"
                    />
                  </CCol>
                  <CCol md="6">
                    <div class="font-weight-bold mb-2">QR Preview</div>
                    <div class="qr-preview border rounded p-3 text-center">
                      <img
                        v-if="givingForm.qr_image_url"
                        :src="givingForm.qr_image_url"
                        alt="Giving QR"
                        class="img-fluid"
                      >
                      <div v-else class="text-muted py-5">No QR image selected yet.</div>
                    </div>
                    <div class="small text-monospace mt-2" v-if="givingForm.qr_image_url">
                      {{ givingForm.qr_image_url }}
                    </div>
                    <div class="mt-3">
                      <CButton color="info" variant="outline" size="sm" class="mr-2" :disabled="qrUploadLoading" @click="triggerQrUpload">
                        {{ qrUploadLoading ? 'Uploading...' : 'Upload New QR' }}
                      </CButton>
                      <input
                        ref="qrUploader"
                        type="file"
                        accept="image/*"
                        class="d-none"
                        @change="handleQrUploadChange"
                      >
                      <small v-if="qrUploadError" class="d-block text-danger mt-2">{{ qrUploadError }}</small>
                    </div>
                  </CCol>
                </CRow>
                <div class="d-flex flex-wrap align-items-center justify-content-between mt-3">
                  <div>
                    <CBadge v-if="givingDirty" color="warning">Unsaved changes</CBadge>
                  </div>
                  <div>
                    <CButton color="success" :disabled="givingSaving || !givingDirty" @click="saveGivingInformation">
                      {{ givingSaving ? 'Saving...' : 'Save' }}
                    </CButton>
                  </div>
                </div>
              </CTab>
              </CTabs>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="12" md="8">
        <CCard>
          <CCardHeader>Certificate Constants</CCardHeader>
          <CCardBody>
            <CertificateConstantsEditor />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <CRow>
      <CCol sm="6" md="8">
        <CCard>
          <CCardHeader>
            Payment Method
          </CCardHeader>
          <CCardBody>
                    <div class="panel-body" id="app">
                        <table class="table table-hover">
                            <thead>
                            <tr>
                                <th>Bank</th>
                                <th>Account</th>
                                <th>Description</th>
                                <th>Action</th>
                            </tr>
                            </thead>
                            <tbody v-sortable.tr="rows">

                            <tr v-for="(arr, index) in form.value" :key="index">
                                <td>
                                    <input class="form-control" v-model="arr.bank"/>
                                </td>
                                <td>
                                    <input class="form-control" v-model="arr.account"/>
                                </td>
                                <td>
                                    <input class="form-control" v-model="arr.description"/>
                                </td>
                                <td>
                                    <CButton  color="info" block size="sm" variant="outline" @click="addRow(index)">Add </CButton>
                                    <CButton  color="danger" block  size="sm" variant="outline" @click="removeRow(index)">Remove</CButton>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <CButton type="submit" v-on:click.prevent="save()"  variant="outline" color="info">Save</CButton>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="6" md="8">
        <CCard>
          <CCardHeader>
            Apps BG Image Setting
          </CCardHeader>
          <CCardBody>
            <CButton  class="ml-3" color="info" @click="openModalPhoto('home_bgimage')"><b>Home BG Image</b></CButton>
            <CButton  class="ml-3" color="info" @click="openModalPhoto('event_bgimage')"><b>Event BG Image</b></CButton>
            <CButton  class="ml-3" color="info" @click="openModalPhoto('location_bgimage')"><b>Location BG Image</b></CButton>
            <CButton  class="ml-3" color="info" @click="openModalPhoto('profile_bgimage')"><b>Profile BG Image</b></CButton>
            <CButton  class="ml-3" color="info" @click="openModalPhoto('kid_bgimage')"><b>iKid BG Image</b></CButton>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="8">
        <CCard>
          <CCardHeader>
            Apps Settings - Join ICare Page
          </CCardHeader>
          <CCardBody>
            <!-- <CRow>
              <CCol>
                <b> Email List to receive information (multiple email separate with comma)</b>
              </CCol>
            </CRow> -->
            <CRow>
              <CCol sm="8">
                <!-- <input class="form-control" v-model="form_email_office.value"/> -->
                <CInput
                  label="Email List to receive information"
                  description="multiple email separate with comma"
                  placeholder=""
                  type="email"
                  horizontal
                  autocomplete="email"
                  v-model="form_email_office.value"
                />
              </CCol>
              <CCol sm="2">
                <CButton type="submit" v-on:click.prevent="save_email_office()"  variant="outline" color="info">Save</CButton>
              </CCol>
            </CRow>
            <hr>
            <CRow>
              <CCol sm="8">
                <!-- <input class="form-control" v-model="form_icarerequest_description.value"/> -->
                <CTextarea
                  label="Description"
                  placeholder=""
                  horizontal
                  rows="5"
                  v-model="form_icarerequest_description.value"
                />
              </CCol>
              <CCol sm="2">
                <CButton type="submit" v-on:click.prevent="save_icarerequest_description()"  variant="outline" color="info">Save</CButton>
              </CCol>
            </CRow>
            <hr>
            <CRow>
              <CCol sm="8">
                <CButton  label="asd" class="ml-3" color="info" @click="openModalPhoto('icarerequest_bgimage')"><b>ICare Request BG Image</b></CButton>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="8">
        <CCard>
          <CCardHeader>
            Apps Settings - Default CSS Discover Webview
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol sm="8">
                <CTextarea
                  placeholder=""
                  horizontal
                  rows="5"
                  v-model="form_css_discover.value"
                />
              </CCol>
              <CCol sm="2">
                <CButton type="submit" v-on:click.prevent="save_css_discover()"  variant="outline" color="info">Save</CButton>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <CRow>
      <CCol sm="8">
        <CCard>
          <CCardHeader>
            Apps Settings - Class Module
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol sm="8">
                <label>List Text Color Class Template</label> <div :style="{backgroundColor: form_class_textcolor_list_setting.value }">current color {{ form_class_textcolor_list_setting.value }}</div>
                <!-- <CButton class="btn btn-sm" type="button" @click="clear('Back Button Text Color')"  variant="outline" color="info">clear</CButton> -->
                <br>
                <colorpicker :color="form_class_textcolor_list_setting.value" v-model="form_class_textcolor_list_setting.value"/>
                <br>
              </CCol>
              <CCol sm="8">
                <label>List Color Class Template</label> <div :style="{backgroundColor: form_class_color_list_setting.value }">current color {{ form_class_color_list_setting.value }}</div>
                <!-- <CButton class="btn btn-sm" type="button" @click="clear('Back Button Text Color')"  variant="outline" color="info">clear</CButton> -->
                <br>
                <colorpicker :color="form_class_color_list_setting.value" v-model="form_class_color_list_setting.value"/>
                <br>
              </CCol>
              <CCol sm="2">
                <CButton type="submit" v-on:click.prevent="save_class_setting()"  variant="outline" color="info">Save</CButton>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
<!-- <pre>{{ $data }}</pre> -->
<alertmodal></alertmodal>
<modalphoto></modalphoto>
<CModal
  :title="faqModal.mode === 'add' ? 'Add FAQ Entry' : 'Edit FAQ Entry'"
  color="info"
  size="lg"
  :show.sync="faqModal.show"
>
  <form @submit.prevent="saveFaqModal">
    <CInput
      label="Question"
      placeholder="What would you like to ask?"
      v-model="faqModal.form.question"
      :maxlength="120"
    />
    <small class="text-muted d-block mb-2">
      {{ (faqModal.form.question || '').length }} / 120 characters
    </small>
    <small v-if="faqModal.errors.question" class="text-danger d-block mb-2">{{ faqModal.errors.question }}</small>
    <CTextarea
      label="Answer"
      rows="6"
      placeholder="Provide the answer shown in the app"
      v-model="faqModal.form.answer"
      :maxlength="2000"
    />
    <small class="text-muted d-block mt-2">
      {{ (faqModal.form.answer || '').length }} / 2000 characters
    </small>
    <small v-if="faqModal.errors.answer" class="text-danger d-block">{{ faqModal.errors.answer }}</small>
  </form>
  <template #footer>
    <CButton color="secondary" type="button" @click="faqModal.show = false">Cancel</CButton>
    <CButton color="info" variant="outline" type="button" @click="saveFaqModal">
      {{ faqModal.mode === 'add' ? 'Add Entry' : 'Save Changes' }}
    </CButton>
  </template>
</CModal>
  </div>
</template>

<script>
import axios from 'axios';
import alertmodal from '@/views/alert/modal.vue';
import EventBus from '@/eventBus';
import Draggable from 'vuedraggable';

import setting from '@/services/setting';
import modalphoto from './modal_photo.vue';
import CertificateConstantsEditor from '@/components/certificate/CertificateConstantsEditor';

export default {
  components: {
    alertmodal,
    modalphoto,
    Draggable,
    CertificateConstantsEditor
  },

  data() {
    const faqLocales = ['en', 'in'];
    const createLocaleMap = (value) => {
      return faqLocales.reduce((acc, locale) => {
        acc[locale] = typeof value === 'function' ? value(locale) : value;
        return acc;
      }, {});
    };

    return {
      faqLocales,
      activeFaqLocale: 'en',
      faqData: createLocaleMap(() => []),
      faqLoadingLocales: createLocaleMap(false),
      faqLoadedLocales: createLocaleMap(false),
      faqDirty: createLocaleMap(false),
      faqSavingLocale: null,
      faqModal: {
        show: false,
        mode: 'add',
        locale: 'en',
        index: null,
        form: {
          question: '',
          answer: ''
        },
        errors: {}
      },
      givingForm: {
        bank_name: '',
        account_number: '',
        account_holder: '',
        currency: 'IDR',
        qr_image_url: '',
        notes: ''
      },
      givingErrors: {},
      givingDirty: false,
      givingSaving: false,
      currencyOptions: [
        { value: 'IDR', text: 'IDR - Indonesian Rupiah' },
        { value: 'USD', text: 'USD - US Dollar' },
        { value: 'SGD', text: 'SGD - Singapore Dollar' },
        { value: 'AUD', text: 'AUD - Australian Dollar' }
      ],
      qrUploadLoading: false,
      qrUploadError: '',
      form: {
        key: '',
        value: [
          { bank: '', account: '', description: '' }
        ]
      },
      form_email_office: {
        key: '',
        value: ''
      },
      form_icarerequest_description: {
        key: '',
        value: ''
      },
      form_css_discover: {
        key: '',
        value: ''
      },
      form_class_color_list_setting: {
        key: '',
        value: ''
      },
      form_class_textcolor_list_setting: {
        key: '',
        value: ''
      }
    };
  },
  mounted() {
    this.loadData();
    this.loadFaqData(this.activeFaqLocale);
    this.loadGivingInformation();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      if (!window.confirm('You have unsaved changes. Leave without saving?')) {
        return next(false);
      }
    }
    next();
  },
  computed: {
    currentFaqEntries() {
      return this.faqData[this.activeFaqLocale] || [];
    },
    hasUnsavedChanges() {
      const faqDirty = this.faqLocales.some(locale => this.faqDirty[locale]);
      return faqDirty || this.givingDirty;
    }
  },
  methods: {
    loadData() {
      setting.getSetting('paymentmethod')
        .then(res => {
          try {
            this.form.value = JSON.parse(res.data.value);
          } catch (error) {
            this.form.value = [{ bank: '', account: '', description: '' }];
          }
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });

      setting.getSetting('email_office')
        .then(res => {
          this.form_email_office.value = res.data.value;
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });

      setting.getSetting('default_css_discover')
        .then(res => {
          this.form_css_discover.value = res.data.value;
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });

      setting.getSetting('class_template_color')
        .then(res => {
          this.form_class_color_list_setting.value = res.data.value;
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });

      setting.getSetting('class_template_textcolor')
        .then(res => {
          this.form_class_textcolor_list_setting.value = res.data.value;
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    loadFaqData(locale) {
      if (!locale) {
        return;
      }
      this.$set(this.faqLoadingLocales, locale, true);
      setting.getSetting(`faq_content_${locale}`)
        .then(res => {
          const parsed = this.parseFaqValue(res.data && res.data.value);
          this.$set(this.faqData, locale, parsed);
          this.$set(this.faqDirty, locale, false);
          this.$set(this.faqLoadedLocales, locale, true);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).finally(() => {
          this.$set(this.faqLoadingLocales, locale, false);
        });
    },
    parseFaqValue(rawValue) {
      if (!rawValue) {
        return [];
      }
      let parsed = [];
      if (Array.isArray(rawValue)) {
        parsed = rawValue;
      } else if (typeof rawValue === 'string') {
        try {
          parsed = JSON.parse(rawValue);
        } catch (error) {
          parsed = [];
        }
      }
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter(item => item)
        .map(item => ({
          question: (item.question || '').toString(),
          answer: (item.answer || '').toString()
        }));
    },
    setFaqLocale(locale) {
      if (this.activeFaqLocale === locale) {
        return;
      }
      this.activeFaqLocale = locale;
      if (!this.faqLoadedLocales[locale] && !this.faqLoadingLocales[locale]) {
        this.loadFaqData(locale);
      }
    },
    openFaqModal(mode, index = null) {
      this.faqModal.mode = mode;
      this.faqModal.locale = this.activeFaqLocale;
      this.faqModal.index = mode === 'edit' ? index : null;
      const items = this.faqData[this.activeFaqLocale] || [];
      const baseEntry = mode === 'edit' && items[index] ? items[index] : { question: '', answer: '' };
      this.faqModal.form = {
        question: baseEntry.question || '',
        answer: baseEntry.answer || ''
      };
      this.faqModal.errors = {};
      this.faqModal.show = true;
    },
    validateFaqForm() {
      const errors = {};
      const question = (this.faqModal.form.question || '').trim();
      const answer = (this.faqModal.form.answer || '').trim();

      if (!question) {
        errors.question = 'Question is required.';
      } else if (question.length < 10 || question.length > 120) {
        errors.question = 'Question must be between 10 and 120 characters.';
      }

      if (!answer) {
        errors.answer = 'Answer is required.';
      } else if (answer.length > 2000) {
        errors.answer = 'Answer must be 2000 characters or less.';
      }

      this.faqModal.errors = errors;
      return Object.keys(errors).length === 0;
    },
    saveFaqModal() {
      if (!this.validateFaqForm()) {
        return;
      }
      const locale = this.faqModal.locale;
      const payload = {
        question: this.faqModal.form.question.trim(),
        answer: this.faqModal.form.answer.trim()
      };
      const entries = this.faqData[locale];
      if (this.faqModal.mode === 'edit' && this.faqModal.index !== null) {
        this.$set(entries, this.faqModal.index, payload);
      } else {
        entries.push(payload);
      }
      this.markFaqDirty(locale);
      this.faqModal.show = false;
    },
    deleteFaq(index) {
      const entries = this.faqData[this.activeFaqLocale];
      if (!entries || !entries[index]) {
        return;
      }
      if (window.confirm('Delete this FAQ entry?')) {
        entries.splice(index, 1);
        this.markFaqDirty(this.activeFaqLocale);
      }
    },
    markFaqDirty(locale) {
      this.$set(this.faqDirty, locale, true);
    },
    onFaqReorder() {
      this.markFaqDirty(this.activeFaqLocale);
    },
    previewAnswer(answer) {
      const clean = (answer || '').trim();
      if (!clean) {
        return 'No answer yet';
      }
      return clean.length > 160 ? `${clean.slice(0, 160)}...` : clean;
    },
    saveFaqEntries() {
      const locale = this.activeFaqLocale;
      if (this.faqSavingLocale) {
        return;
      }
      const payload = {
        key: `faq_content_${locale}`,
        value: JSON.stringify(this.faqData[locale] || [])
      };
      this.faqSavingLocale = locale;
      setting.saveSetting(payload)
        .then(res => {
          this.$set(this.faqDirty, locale, false);
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).finally(() => {
          this.faqSavingLocale = null;
        });
    },
    loadGivingInformation() {
      setting.getSetting('giving_information')
        .then(res => {
          let parsed = {};
          try {
            parsed = res.data && res.data.value ? JSON.parse(res.data.value) : {};
          } catch (error) {
            parsed = {};
          }
          this.givingForm = {
            ...this.givingForm,
            ...parsed,
            currency: (parsed && parsed.currency) || 'IDR'
          };
          this.givingDirty = false;
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    markGivingDirty() {
      if (!this.givingDirty) {
        this.givingDirty = true;
      }
    },
    validateGivingForm() {
      const errors = {};
      if (!(this.givingForm.bank_name || '').trim()) {
        errors.bank_name = 'Bank name is required.';
      }
      if (!(this.givingForm.account_number || '').trim()) {
        errors.account_number = 'Account number is required.';
      }
      if (!(this.givingForm.account_holder || '').trim()) {
        errors.account_holder = 'Account holder is required.';
      }
      if (!(this.givingForm.currency || '').trim()) {
        errors.currency = 'Currency is required.';
      }
      if (!(this.givingForm.qr_image_url || '').trim()) {
        errors.qr_image_url = 'QR image URL is required.';
      } else if (!this.isValidUrl(this.givingForm.qr_image_url)) {
        errors.qr_image_url = 'Please enter a valid URL.';
      }
      this.givingErrors = errors;
      return Object.keys(errors).length === 0;
    },
    isValidUrl(value) {
      try {
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
      } catch (error) {
        return false;
      }
    },
    saveGivingInformation() {
      if (!this.validateGivingForm()) {
        return;
      }
      this.givingSaving = true;
      const payload = {
        key: 'giving_information',
        value: JSON.stringify(this.givingForm)
      };
      setting.saveSetting(payload)
        .then(res => {
          this.givingDirty = false;
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).finally(() => {
          this.givingSaving = false;
        });
    },
    triggerQrUpload() {
      this.qrUploadError = '';
      if (this.qrUploadLoading) {
        return;
      }
      if (this.$refs.qrUploader) {
        this.$refs.qrUploader.click();
      }
    },
    handleQrUploadChange(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null;
      if (!file) {
        return;
      }
      this.uploadGivingQrFile(file).finally(() => {
        if (event && event.target) {
          event.target.value = '';
        }
      });
    },
    uploadGivingQrFile(file) {
      this.qrUploadLoading = true;
      this.qrUploadError = '';
      const formData = new FormData();
      formData.append('file', file);
      return axios.post('/upload_image_tinymce', formData)
        .then(res => {
          const payload = res.data || {};
          const url = payload.location || payload.url || (payload.data && (payload.data.location || payload.data.url)) || res.location;
          if (!url) {
            throw new Error('Upload succeeded but URL was not provided.');
          }
          this.givingForm.qr_image_url = url;
          this.markGivingDirty();
        }).catch((err) => {
          this.qrUploadError = err.response && err.response.data && err.response.data.message ? err.response.data.message : err.message;
        }).finally(() => {
          this.qrUploadLoading = false;
        });
    },
    handleBeforeUnload(event) {
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    save() {
      this.form.key = 'paymentmethod';
      setting.saveSetting(this.form)
        .then(res => {
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    save_email_office() {
      this.form_email_office.key = 'email_office';
      setting.saveSetting(this.form_email_office)
        .then(res => {
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    save_icarerequest_description() {
      this.form_icarerequest_description.key = 'icarerequest_description';
      setting.saveSetting(this.form_icarerequest_description)
        .then(res => {
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    save_css_discover() {
      this.form_css_discover.key = 'default_css_discover';
      setting.saveSetting(this.form_css_discover)
        .then(res => {
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    save_class_setting() {
      this.form_class_color_list_setting.key = 'class_template_color';
      this.form_class_textcolor_list_setting.key = 'class_template_textcolor';
      setting.saveSetting(this.form_class_color_list_setting)
        .then(res => {
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });

      setting.saveSetting(this.form_class_textcolor_list_setting)
        .then(res => {
          var params = { title: 'Success', mode: 'success', data: res.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        }).catch((err) => {
          var params = { title: 'Error', mode: 'danger', data: err.response.data.message };
          EventBus.$emit('OPEN_MODAL_ALERT', params);
        });
    },
    addRow(index) {
      try {
        this.form.value.splice(index + 1, 0, {});
      } catch (e) {
        console.log(e);
      }
    },
    removeRow(index) {
      this.form.value.splice(index, 1);
    },
    openModalPhoto(key) {
      if (key === 'home_bgimage') { var title = 'Home Page'; }
      if (key === 'event_bgimage') { var title = 'Event Page'; }
      if (key === 'location_bgimage') { var title = 'Location Page'; }
      if (key === 'profile_bgimage') { var title = 'Profile Page'; }
      if (key === 'icarerequest_bgimage') { var title = 'ICare Request Page'; }
      if (key === 'kid_bgimage') { var title = 'iKid Page'; }
      var params = { title: 'Global Image | ' + title, mode: 'edit', data: key };
      EventBus.$emit('OPEN_MODAL_PHOTO', params);
      console.log('load modal photo');
    }
  }
};
</script>

<style scoped>
.faq-row-handle {
  cursor: move;
  color: #6c757d;
}

.faq-answer-preview {
  max-height: 4.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.qr-preview img {
  max-height: 260px;
  width: auto;
}

.systemsettings-tabs-wrapper {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.03);
}

.systemsettings-tabs {
  margin: 0;
}

.systemsettings-tabs >>> .nav-tabs {
  border-bottom: 1px solid #dee2e6;
  background-color: #fff;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
}

.systemsettings-tabs >>> .nav-tabs .nav-link {
  border: none;
  border-radius: 0;
  color: #6c757d;
  font-weight: 600;
  padding: 0.5rem 1rem;
  margin-right: 0.5rem;
}

.systemsettings-tabs >>> .nav-tabs .nav-link.active {
  color: #17a2b8;
  border-bottom: 2px solid #17a2b8;
  background-color: transparent;
}

.systemsettings-tabs >>> .tab-content {
  margin-top: 0;
  background-color: #fff;
  border-radius: 0.35rem;
  padding-top: 0.5rem;
}
</style>
